import React, { useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import { FixedSpacer, Typography } from '@grammarly/badger-ui'
import { SocialShareWidget } from '@components/share-widget/social-share-widget'
import { CtaButtonDescription } from '@components/item/cta-button'
import { ContentProps, ContentRenderer } from '@components/renderer'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'

import { useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'
import { useMediaQuery } from '@lib/hooks/media-query'
import { TypeSectionHero, SectionHeroLayoutDir } from '@lib/types'
import { isUnresolvedLink } from '@lib/utils/contentful-entry'
import { isTypeComponentEmptyStub } from '@lib/types/content-types'
import { getDescriptionComponent } from './title'
import { MobileAgentDisplay } from '@components/dynamic/bts-agents-25'

import heroStyles from './hero.module.scss'
import btsStyles from '@components/dynamic/bts-agents-25/bts-agents-25.module.scss'

export const AnimatedHeroTitle: React.FC<ContentProps<TypeSectionHero>> = (props) => {
  const {
    structure: {
      fields: { layoutDirection, titleSize, description, ctAs, socialShareWidget },
    },
  } = props

  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()
  const isTablet = useMediaQuery('(max-width: 768px)')

  // Refs to store cleanup handles
  const timeoutsRef = useRef<NodeJS.Timeout[]>([])
  const rafIdRef = useRef<number | null>(null)

  // Animation state management for sequential reveal
  const [animationStates, setAnimationStates] = useState({
    eyebrow: false,
    seoTitle: false,
    titleLine1: false,
    titleLine2: false,
    description: false,
    ctas: false,
    ctaDescriptions: false,
    socialShare: false,
  })

  // Trigger animations sequentially - start immediately, don't wait for Spline
  useEffect(() => {
    // Start animations immediately for better user experience - no dependency on Spline loading
    // Use requestAnimationFrame to ensure proper timing on mobile devices
    const startAnimations = () => {
      const timeouts = [
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, eyebrow: true })), 100), // Slightly longer delay for mobile stability
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, seoTitle: true })), 200),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, titleLine1: true })), 350),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, titleLine2: true })), 500),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, description: true })), 700),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, ctas: true })), 1100),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, ctaDescriptions: true })), 1250),
        setTimeout(() => setAnimationStates((prev) => ({ ...prev, socialShare: true })), 1000),
      ]

      // Store timeouts in ref for cleanup
      timeoutsRef.current = timeouts
    }

    // Use RAF for smoother animation start on mobile
    rafIdRef.current = requestAnimationFrame(() => {
      startAnimations()
    })

    return () => {
      // Clean up RAF
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }

      // Clean up all timeouts
      timeoutsRef.current.forEach(clearTimeout)
      timeoutsRef.current = []
    }
  }, []) // Run once on mount, don't wait for Spline loading

  // Use T1 for consistency with bespoke hero, regardless of titleSize prop
  const TitleComponent = Typography.T1
  const DescriptionComponent = getDescriptionComponent(titleSize)

  return (
    <figcaption>
      <header>
        {/* School Year Eyebrow with Badge */}
        <div
          className={cn(btsStyles.btsHeroEyebrow, {
            [btsStyles.animate]: animationStates.eyebrow,
          })}
        >
          <div className={btsStyles.btsHeroBadge}>
            <span>New</span>
          </div>
          <div className={btsStyles.btsHeroEyebrowText}>Just in time for the new school year</div>
        </div>

        <>
          <TitleComponent as="h1" className={btsStyles.btsHeroTitle}>
            <span
              className={cn(btsStyles.btsTitleContent, {
                [btsStyles.animate]: animationStates.titleLine1,
              })}
            >
              The tools you wish
            </span>
            <span
              className={cn(btsStyles.btsTitleContent, {
                [btsStyles.animate]: animationStates.titleLine2,
              })}
            >
              you had last semester
            </span>
          </TitleComponent>
        </>

        <FixedSpacer multiplier={isTablet ? 2 : 3} />
        <DescriptionComponent
          as="div"
          className={cn(btsStyles.btsHeroDescription, {
            [btsStyles.fadeIn]: animationStates.description,
          })}
        >
          {renderRichTextMultiLineContent(description)}
        </DescriptionComponent>

        {/* Insert mobile agents carousel between subhead and CTAs */}
        <div className={btsStyles.mobileAgentsOnly}>
          <FixedSpacer multiplier={isTablet ? 3 : 4} />
          <MobileAgentDisplay />
          <FixedSpacer multiplier={isTablet ? 3 : 4} />
        </div>
      </header>
      {ctAs?.length && (
        <>
          <FixedSpacer multiplier={isTablet ? 3 : 4} />
          <div
            className={cn(heroStyles.ctasContainer, btsStyles.btsCtaContainer, btsStyles.hiddenBeforeAnimate, {
              [heroStyles.ctasCentered]:
                layoutDirection === SectionHeroLayoutDir.Vertical ||
                layoutDirection === SectionHeroLayoutDir.VerticalWide,
              [btsStyles.fadeIn]: animationStates.ctas,
            })}
          >
            <ContentRenderer structure={ctAs} />
          </div>
          <div
            className={cn(
              heroStyles.ctasDescriptionsContainer,
              btsStyles.btsCtaDescriptionsContainer,
              btsStyles.hiddenBeforeAnimate,
              {
                [btsStyles.fadeIn]: animationStates.ctaDescriptions,
              }
            )}
          >
            {ctAs
              .map(getAuthAndDeviceTypeCta)
              .filter((cta) => !isUnresolvedLink(cta))
              .filter((cta) => !isTypeComponentEmptyStub(cta))
              .map((cta) => (
                <CtaButtonDescription key={`description-${cta.sys.contentType.sys.id}-${cta.sys.id}`} structure={cta} />
              ))}
          </div>
        </>
      )}
      {socialShareWidget && (
        <>
          <FixedSpacer multiplier={5} />
          <div className={cn(btsStyles.hiddenBeforeAnimate, { [btsStyles.fadeIn]: animationStates.socialShare })}>
            <SocialShareWidget structure={socialShareWidget} />
          </div>
        </>
      )}
    </figcaption>
  )
}
